import React from "react";
import { Link as RouterLink } from "react-router-dom";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

const HELP_LINK_RE = /(\/help(?:\?[a-zA-Z0-9_=&%-]+)?)/g;
/** Rotas internas da app (deep links de busca / registros / assistente). */
const APP_PATH_RE =
  /(\/(?:clients|products|chat|equipe(?:\/[a-z0-9_-]+)*|popup-equipe(?:\/[a-z0-9_-]+)*|settings\/[a-z0-9_-]+|agents|chatbots|team\/[a-z0-9_-]+|sales-pipeline)(?:\/[a-zA-Z0-9_-]+)?(?:\?[a-zA-Z0-9_=&%-]+)?)/g;
const APP_PATH_TEST =
  /^\/(?:clients|products|chat|equipe(?:\/[a-z0-9_-]+)*|popup-equipe(?:\/[a-z0-9_-]+)*|settings\/[a-z0-9_-]+|agents|chatbots|team\/[a-z0-9_-]+|sales-pipeline)(?:\/[a-zA-Z0-9_-]+)?(?:\?[a-zA-Z0-9_=&%-]+)?$/;
/** Negrito, itálico simples e código — o subconjunto que o assistente usa. */
const INLINE_RE = /(\*\*[^*]+\*\*|`[^`]+`)/g;

/**
 * Renderiza a resposta do assistente.
 *
 * O modelo escreve em markdown, mas aqui ele é interpretado por um subconjunto
 * fechado (título, lista, negrito, código) montado em componentes React. Nada
 * de HTML: o texto vem de um LLM que leu conteúdo de cliente, então nem em
 * último caso ele deve poder injetar marcação na página.
 */

function AppPathLink({ href, children, onNavigate }) {
  // Platform AI passa onNavigate (mapeia /team → /equipe, fecha o diálogo).
  // Internal Ask e demais callers sem callback: RouterLink (comportamento antigo).
  if (typeof onNavigate === "function") {
    return (
      <Link
        component="button"
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onNavigate(href);
        }}
        underline="hover"
        sx={{
          wordBreak: "break-all",
          verticalAlign: "baseline",
          font: "inherit",
          cursor: "pointer",
        }}
      >
        {children}
      </Link>
    );
  }
  return (
    <Link
      component={RouterLink}
      to={href}
      underline="hover"
      sx={{ wordBreak: "break-all" }}
    >
      {children}
    </Link>
  );
}

function renderInline(text, keyPrefix, onNavigate) {
  const nodes = [];

  String(text)
    .split(INLINE_RE)
    .forEach((chunk, i) => {
      if (!chunk) return;

      if (chunk.startsWith("**") && chunk.endsWith("**")) {
        nodes.push(
          <Box component="strong" key={`${keyPrefix}-b-${i}`} sx={{ fontWeight: 600 }}>
            {chunk.slice(2, -2)}
          </Box>
        );
        return;
      }

      if (chunk.startsWith("`") && chunk.endsWith("`")) {
        nodes.push(
          <Box
            component="code"
            key={`${keyPrefix}-c-${i}`}
            sx={{
              px: 0.5,
              borderRadius: 0.5,
              bgcolor: "action.hover",
              fontFamily: "monospace",
              fontSize: "0.85em",
            }}
          >
            {chunk.slice(1, -1)}
          </Box>
        );
        return;
      }

      chunk.split(HELP_LINK_RE).forEach((part, j) => {
        if (!part) return;
        if (part.startsWith("/help")) {
          nodes.push(
            <AppPathLink key={`${keyPrefix}-h-${i}-${j}`} href={part} onNavigate={onNavigate}>
              {part}
            </AppPathLink>
          );
          return;
        }
        part.split(APP_PATH_RE).forEach((seg, k) => {
          if (!seg) return;
          if (APP_PATH_TEST.test(seg)) {
            nodes.push(
              <AppPathLink key={`${keyPrefix}-a-${i}-${j}-${k}`} href={seg} onNavigate={onNavigate}>
                {seg}
              </AppPathLink>
            );
            return;
          }
          nodes.push(
            <React.Fragment key={`${keyPrefix}-t-${i}-${j}-${k}`}>{seg}</React.Fragment>
          );
        });
      });
    });

  return nodes;
}

/** Agrupa as linhas em blocos: título, item de lista ou parágrafo. */
function parseBlocks(text) {
  const blocks = [];
  const lines = String(text).split(/\r?\n/);

  for (const raw of lines) {
    const line = raw.replace(/\s+$/, "");

    if (!line.trim()) {
      blocks.push({ type: "spacer" });
      continue;
    }

    const heading = line.match(/^(#{1,4})\s+(.*)$/);
    if (heading) {
      blocks.push({ type: "heading", level: heading[1].length, text: heading[2] });
      continue;
    }

    const bullet = line.match(/^\s*[-*•]\s+(.*)$/);
    if (bullet) {
      blocks.push({ type: "item", text: bullet[1] });
      continue;
    }

    const numbered = line.match(/^\s*(\d{1,2})[.)]\s+(.*)$/);
    if (numbered) {
      blocks.push({ type: "item", marker: `${numbered[1]}.`, text: numbered[2] });
      continue;
    }

    blocks.push({ type: "paragraph", text: line });
  }

  // Linha em branco no fim ou entre dois blocos consecutivos não rende nada.
  return blocks.filter((b, i) => b.type !== "spacer" || (i > 0 && i < blocks.length - 1));
}

export default function AssistantMessageContent({ content, variant = "body2", sx, onNavigate }) {
  const text = String(content || "");
  if (!text) return null;

  const blocks = parseBlocks(text);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, ...sx }}>
      {blocks.map((block, idx) => {
        if (block.type === "spacer") {
          return <Box key={`sp-${idx}`} sx={{ height: 6 }} />;
        }

        if (block.type === "heading") {
          return (
            <Typography
              key={`h-${idx}`}
              variant={variant}
              component="div"
              sx={{ fontWeight: 700, mt: idx === 0 ? 0 : 0.5 }}
            >
              {renderInline(block.text, `h-${idx}`, onNavigate)}
            </Typography>
          );
        }

        if (block.type === "item") {
          return (
            <Box key={`i-${idx}`} sx={{ display: "flex", gap: 0.75, pl: 0.5 }}>
              <Typography variant={variant} component="span" sx={{ opacity: 0.7 }}>
                {block.marker || "•"}
              </Typography>
              <Typography variant={variant} component="span">
                {renderInline(block.text, `i-${idx}`, onNavigate)}
              </Typography>
            </Box>
          );
        }

        return (
          <Typography key={`p-${idx}`} variant={variant} component="div">
            {renderInline(block.text, `p-${idx}`, onNavigate)}
          </Typography>
        );
      })}
    </Box>
  );
}

export { parseBlocks };
